import { UseFormReturn } from 'react-hook-form'
import { FormControl, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { DraggableFieldArray } from '@/components/shared/DraggableFieldArray'
import { LinkRowEditor } from '@/components/shared/LinkRowEditor'
import { blankLinkRow } from '@/lib/linkRow'
import { FooterLinkFormValues } from '../types'

interface FooterColumnEditorProps {
  form: UseFormReturn<any>
  index: number
  languageId: number
  allLanguages: string[]
}

function linkTitle(link: FooterLinkFormValues, index: number, languageId: number): string {
  const label =
    link.profiles[languageId]?.label?.trim() ||
    link.profiles.find(p => p.label?.trim())?.label
  return label?.trim() ? label : `Link #${index + 1}`
}

// Rendered as the expanded body of the OUTER (columns) `DraggableFieldArray`
// row — see `FooterSettings`. The nested links list below is a SECOND
// `DraggableFieldArray`, but it does NOT get its own `DragDropContext`:
// react-beautiful-dnd doesn't support nesting contexts, so `FooterSettings`
// renders ONE context for the whole page and routes reorders by
// `droppableId` (`footer-columns` vs `footer-links-${index}`) — see its
// `handleDragEnd`.
export function FooterColumnEditor({ form, index, languageId, allLanguages }: FooterColumnEditorProps) {
  const base = `columns.${index}`

  const createBlankLink = (): FooterLinkFormValues => blankLinkRow(allLanguages)

  return (
    <div className="space-y-4">
      <InputFieldContainer
        label="Heading"
        control={form.control}
        name={`${base}.profiles.${languageId}.heading`}
        render={({ field }) => (
          <FormItem className="col-span-3">
            <FormControl>
              <Input
                placeholder="e.g., Customer Service"
                type="text"
                {...field}
                value={field.value ?? ''}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div>
        <p className="text-sm font-medium mb-2">Links</p>
        <DraggableFieldArray
          form={form}
          name={`${base}.links`}
          droppableId={`footer-links-${index}`}
          onAdd={createBlankLink}
          itemTitle={(link, linkIndex) => linkTitle(link, linkIndex, languageId)}
          renderItem={linkIndex => (
            <LinkRowEditor
              form={form}
              basePath={`${base}.links.${linkIndex}`}
              languageId={languageId}
            />
          )}
          addLabel="Add link"
          emptyText="No links yet."
        />
      </div>
    </div>
  )
}
